/**
 * Table-vs-table collision for the editor.
 *
 * The rule is physical: two tables collide when their solid *tops* intersect,
 * or when a chair of one stands on the other's top. Chair rings brushing each
 * other is deliberately legal — that is what a real hall's tight aisle looks
 * like, and what the floor-plan import produces — so recreating a dense venue
 * by hand stays possible. Nothing here is mirrored in the backend: the canvas
 * is where furniture is moved, so the canvas is where it settles; the PDF just
 * draws what was saved.
 */

import { SEAT_RADIUS, computeTableGeometry, halfExtent, seatWorldPositions } from './geometry'
import type { TableShape } from './types'

export interface TablePlacement {
  shape: TableShape
  seatCount: number
  x: number
  y: number
  rotation: number
}

interface Pt {
  x: number
  y: number
}

/** A table reduced to what can collide: its top, its chair discs, one radius
 * that bounds them all (for cheap pair rejection), and its centre. */
interface Solid {
  cx: number
  cy: number
  reach: number
  topCircle: { x: number; y: number; r: number } | null
  topPoly: Pt[] | null
  chairs: Pt[]
}

function corners(cx: number, cy: number, halfW: number, halfH: number, degrees: number): Pt[] {
  const rad = (degrees * Math.PI) / 180
  const cos = Math.cos(rad)
  const sin = Math.sin(rad)
  return [
    { x: -halfW, y: -halfH },
    { x: -halfW, y: halfH },
    { x: halfW, y: halfH },
    { x: halfW, y: -halfH },
  ].map((p) => ({ x: cx + p.x * cos - p.y * sin, y: cy + p.x * sin + p.y * cos }))
}

function solidOf(table: TablePlacement): Solid {
  const geometry = computeTableGeometry(table.shape, table.seatCount)
  const half = halfExtent(geometry)
  const chairs = seatWorldPositions(
    table.shape,
    table.seatCount,
    table.x,
    table.y,
    table.rotation,
  ).map((seat) => ({ x: seat.x, y: seat.y }))
  const round = table.shape === 'round'
  return {
    cx: table.x,
    cy: table.y,
    reach: Math.hypot(half.x, half.y),
    topCircle: round ? { x: table.x, y: table.y, r: geometry.width / 2 } : null,
    topPoly: round
      ? null
      : corners(table.x, table.y, geometry.width / 2, geometry.height / 2, table.rotation),
    chairs,
  }
}

function shifted(solid: Solid, dx: number, dy: number): Solid {
  if (dx === 0 && dy === 0) return solid
  return {
    cx: solid.cx + dx,
    cy: solid.cy + dy,
    reach: solid.reach,
    topCircle: solid.topCircle && {
      x: solid.topCircle.x + dx,
      y: solid.topCircle.y + dy,
      r: solid.topCircle.r,
    },
    topPoly: solid.topPoly && solid.topPoly.map((p) => ({ x: p.x + dx, y: p.y + dy })),
    chairs: solid.chairs.map((p) => ({ x: p.x + dx, y: p.y + dy })),
  }
}

// --- primitives (all strict: touching is not colliding) ----------------------

function segmentDistance(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const span = dx * dx + dy * dy
  const t = span === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / span))
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy))
}

function pointInConvex(p: Pt, poly: Pt[]): boolean {
  let sign = 0
  for (let i = 0; i < poly.length; i += 1) {
    const a = poly[i]
    const b = poly[(i + 1) % poly.length]
    const cross = (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x)
    if (cross !== 0) {
      if (sign !== 0 && Math.sign(cross) !== sign) return false
      sign = Math.sign(cross)
    }
  }
  return true
}

function circleCircle(a: { x: number; y: number; r: number }, b: { x: number; y: number; r: number }): boolean {
  const dx = a.x - b.x
  const dy = a.y - b.y
  const reach = a.r + b.r
  return dx * dx + dy * dy < reach * reach
}

function circlePoly(circle: { x: number; y: number; r: number }, poly: Pt[]): boolean {
  if (pointInConvex(circle, poly)) return true
  for (let i = 0; i < poly.length; i += 1) {
    if (segmentDistance(circle, poly[i], poly[(i + 1) % poly.length]) < circle.r) return true
  }
  return false
}

/** Separating-axis test for two convex polygons. */
function polyPoly(a: Pt[], b: Pt[]): boolean {
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i += 1) {
      const p = poly[i]
      const q = poly[(i + 1) % poly.length]
      const nx = p.y - q.y
      const ny = q.x - p.x
      let minA = Infinity
      let maxA = -Infinity
      for (const v of a) {
        const dot = nx * v.x + ny * v.y
        minA = Math.min(minA, dot)
        maxA = Math.max(maxA, dot)
      }
      let minB = Infinity
      let maxB = -Infinity
      for (const v of b) {
        const dot = nx * v.x + ny * v.y
        minB = Math.min(minB, dot)
        maxB = Math.max(maxB, dot)
      }
      if (maxA <= minB || maxB <= minA) return false
    }
  }
  return true
}

function topsIntersect(a: Solid, b: Solid): boolean {
  if (a.topCircle && b.topCircle) return circleCircle(a.topCircle, b.topCircle)
  if (a.topCircle && b.topPoly) return circlePoly(a.topCircle, b.topPoly)
  if (b.topCircle && a.topPoly) return circlePoly(b.topCircle, a.topPoly)
  if (a.topPoly && b.topPoly) return polyPoly(a.topPoly, b.topPoly)
  return false
}

function chairOnTop(chairs: Pt[], top: Solid): boolean {
  for (const chair of chairs) {
    const disc = { x: chair.x, y: chair.y, r: SEAT_RADIUS }
    if (top.topCircle ? circleCircle(disc, top.topCircle) : circlePoly(disc, top.topPoly ?? []))
      return true
  }
  return false
}

function solidsCollide(a: Solid, b: Solid): boolean {
  const dx = a.cx - b.cx
  const dy = a.cy - b.cy
  const reach = a.reach + b.reach
  if (dx * dx + dy * dy > reach * reach) return false
  return topsIntersect(a, b) || chairOnTop(a.chairs, b) || chairOnTop(b.chairs, a)
}

// --- public API ---------------------------------------------------------------

export function tablesCollide(a: TablePlacement, b: TablePlacement): boolean {
  return solidsCollide(solidOf(a), solidOf(b))
}

/** Every colliding pair, each listed once — what the export dialog warns about. */
export function overlappingPairs<T extends TablePlacement>(tables: T[]): [T, T][] {
  const solids = tables.map(solidOf)
  const pairs: [T, T][] = []
  for (let i = 0; i < tables.length; i += 1) {
    for (let j = i + 1; j < tables.length; j += 1) {
      if (solidsCollide(solids[i], solids[j])) pairs.push([tables[i], tables[j]])
    }
  }
  return pairs
}

// How the landing spot is searched: rings of candidates at every RADIUS_STEP,
// angles tried nearest-first to the direction pointing away from whatever was
// hit, so the nudge goes the way a person would expect. The first clean
// candidate on the smallest ring wins, which makes the result deterministic
// and keeps the hop as short as the grid allows.
const RADIUS_STEP = 8
const ANGLE_STEP = 15
const DEFAULT_MAX_SHIFT = 600

/**
 * The translation that takes the whole moved selection to the nearest clean
 * spot, as one rigid piece — `{dx: 0, dy: 0}` when it already rests clean, and
 * `null` when nothing within `maxShift` is clean (a jammed room: the drop
 * stands, and the export warning carries it from there).
 *
 * Members of the selection are never tested against each other: a formation is
 * moved as furniture, not rearranged.
 */
export function separateFrom(
  moved: TablePlacement[],
  others: TablePlacement[],
  options?: { maxShift?: number },
): { dx: number; dy: number } | null {
  if (moved.length === 0 || others.length === 0) return { dx: 0, dy: 0 }
  const maxShift = options?.maxShift ?? DEFAULT_MAX_SHIFT
  const movedSolids = moved.map(solidOf)
  const otherSolids = others.map(solidOf)

  const collidesAt = (dx: number, dy: number): boolean => {
    for (const solid of movedSolids) {
      const at = shifted(solid, dx, dy)
      for (const obstacle of otherSolids) {
        if (solidsCollide(at, obstacle)) return true
      }
    }
    return false
  }

  if (!collidesAt(0, 0)) return { dx: 0, dy: 0 }

  // Push away from the first obstacle actually hit; dead-centre drops fall
  // back to +x so the answer is still deterministic.
  let away = { x: 1, y: 0 }
  outer: for (const solid of movedSolids) {
    for (const obstacle of otherSolids) {
      if (solidsCollide(solid, obstacle)) {
        const vx = solid.cx - obstacle.cx
        const vy = solid.cy - obstacle.cy
        const span = Math.hypot(vx, vy)
        if (span > 1e-6) away = { x: vx / span, y: vy / span }
        break outer
      }
    }
  }

  const base = Math.atan2(away.y, away.x)
  const offsets: number[] = [0]
  for (let step = 1; step * ANGLE_STEP < 180; step += 1) {
    offsets.push(step * ANGLE_STEP, -step * ANGLE_STEP)
  }
  offsets.push(180)

  for (let radius = RADIUS_STEP; radius <= maxShift; radius += RADIUS_STEP) {
    for (const offset of offsets) {
      const angle = base + (offset * Math.PI) / 180
      const dx = radius * Math.cos(angle)
      const dy = radius * Math.sin(angle)
      if (!collidesAt(dx, dy)) return { dx, dy }
    }
  }
  return null
}
